import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { API_URL, enrichedFetch } from '../../services/api';
import { PromptSortOptions, PromptView } from '../../Types/enums';
import { CreatePrompt, PatchPromptOpts, Prompt, PromptAPIResponse } from '../../Types/prompt';
import { PromptLabelCount } from '../../Types/tags';
import { PromptLabelsPathUrl } from '../../util/routesUrls';
import { stringArrayToURLQueryParam } from '../../util/stringUtils';
import { myFolder } from '../consts';
import {
  setCurrentPrompt,
  setIsLoading,
  setLabelOptions,
  setPage,
} from '../reducers/promptReducer';
import { AppDispatch, RootState } from '../store';

export const getPromptlabels = createAsyncThunk<PromptLabelCount[], void, { state: RootState }>(
  'prompts/getPromptlabels',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const {
      publishedOnly,
      myPromptsOnly,
      recentOnly,
      pinned,
      selectedLabels,
      excludedLabels,
      searchQuery,
      folderPinned,
      promptView,
      archivedOnly,
    } = getState().prompts;
    const { currentFolder } = getState().folders;
    const { selectedApp } = getState().appStatus;

    let url = `${API_URL}/${PromptLabelsPathUrl()}?recentOnly=${recentOnly}&app=${selectedApp}`;

    if (publishedOnly !== undefined) {
      url += `&publishedOnly=${publishedOnly}`;
    }

    if (myPromptsOnly !== undefined) {
      url += `&myPromptsOnly=${myPromptsOnly}`;
    }

    if (pinned !== undefined) {
      url += `&pinned=${pinned}`;
    }

    if (folderPinned && currentFolder && currentFolder.id !== myFolder.id) {
      url += `&folderId=${currentFolder.id}`;
    }

    if (selectedLabels.length) {
      const labelsQuery = stringArrayToURLQueryParam('labels', selectedLabels);
      url += `&${labelsQuery}`;
    }

    if (excludedLabels.length) {
      const excludeQuery = stringArrayToURLQueryParam('excludedLabels', excludedLabels);
      url += `&${excludeQuery}`;
    }

    if (searchQuery) {
      url += `&q=${searchQuery}`;
    }

    if (archivedOnly) {
      url += `&archivedOnly=${archivedOnly}`;
    }

    if (promptView === PromptView.SHARED) {
      url += `&shared=true`;
    }

    try {
      const response = (await enrichedFetch(url)) as PromptLabelCount[];
      dispatch(
        setLabelOptions(
          response.map((labelOption) => ({
            ...labelOption,
            count: Number(labelOption.count),
          }))
        )
      );

      return response;
    } catch {
      enqueueSnackbar('Failed to fetch labels', { variant: 'error' });
      return rejectWithValue('Failed to fetch labels');
    }
  }
);

export const findPrompts = createAsyncThunk<PromptAPIResponse, void, { state: RootState }>(
  'prompts/findPrompts',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const {
      publishedOnly,
      myPromptsOnly,
      sortOption,
      pageOffset,
      searchQuery,
      sortDirection,
      selectedLabels,
      excludedLabels,
      featuredOnly,
      liked,
      recentOnly,
      pinned,
      promptView,
      archivedOnly,
      folderPinned,
      limit,
    } = getState().prompts;
    const { currentFolder } = getState().folders;
    const { selectedApp } = getState().appStatus;

    dispatch(setIsLoading(true));
    let url = `${API_URL}/prompts?recentOnly=${recentOnly}&sort=${sortOption}&offset=${pageOffset}&app=${selectedApp}`;

    if (publishedOnly !== undefined) {
      url += `&publishedOnly=${publishedOnly}`;
    }

    if (myPromptsOnly !== undefined) {
      url += `&myPromptsOnly=${myPromptsOnly}`;
    }

    if (featuredOnly) {
      url += `&featuredOnly=${featuredOnly}`;
    }
    if (pinned !== undefined) {
      url += `&pinned=${pinned}`;
    }
    if (currentFolder && currentFolder.id !== myFolder.id) {
      url += `&folderId=${currentFolder.id}`;
    }
    if (folderPinned !== undefined) {
      url += `&folderPinned=${folderPinned}`;
    }
    if (liked !== undefined) {
      url += `&liked=${liked}`;
    }

    if (searchQuery) {
      url += `&q=${searchQuery}`;
    }

    if (sortOption !== PromptSortOptions.DEFAULT) {
      url += `&sortDirection=${sortDirection}`;
    }

    if (limit !== undefined) {
      url += `&limit=${limit}`;
    }

    if (selectedLabels.length) {
      const labelsQuery = stringArrayToURLQueryParam('labels', selectedLabels);
      url += `&${labelsQuery}`;
    }

    if (excludedLabels.length) {
      const excludeQuery = stringArrayToURLQueryParam('excludedLabels', excludedLabels);
      url += `&${excludeQuery}`;
    }

    if (promptView === PromptView.SHARED) {
      url += `&shared=true`;
    }

    if (archivedOnly !== undefined) {
      url += `&archivedOnly=${archivedOnly}`;
    }

    try {
      const response = (await enrichedFetch(url)) as PromptAPIResponse;
      dispatch(getPromptlabels());
      dispatch(setIsLoading(false));
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch prompts', { variant: 'error' });
      return rejectWithValue('Failed to fetch prompts');
    }
  }
);

export const findFeaturedPrompts = createAsyncThunk<Prompt[], void, { state: RootState }>(
  'prompts/findFeaturedPrompts',
  async (_, { rejectWithValue }) => {
    const url = `${API_URL}/prompts/findFeaturedPrompts`;

    try {
      const response = (await enrichedFetch(url)) as Prompt[];
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch the featured prompts', { variant: 'error' });
      return rejectWithValue('Failed to fetch the featured prompts');
    }
  }
);

export const findPrompt = createAsyncThunk<Prompt, Prompt['id'], { state: RootState }>(
  'prompts/findPrompt',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/prompts/prompt/${id}`)) as Prompt;

      dispatch(setCurrentPrompt(response));

      return response;
    } catch {
      enqueueSnackbar('Failed to fetch prompt', { variant: 'error' });
      return rejectWithValue('Failed to fetch prompt');
    }
  }
);

export const updatePrompt = createAsyncThunk<Prompt, { prompt: PatchPromptOpts; promptId: string }>(
  'prompts/updatePrompt',
  async ({ prompt, promptId }, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/prompts/${promptId}`, {
        method: 'PATCH',
        body: JSON.stringify(prompt),
        headers: {
          'Content-Type': 'application/json',
        },
      })) as Prompt;
      return response;
    } catch {
      enqueueSnackbar('Failed to update prompt', { variant: 'error' });
      return rejectWithValue('Failed to update prompt');
    }
  }
);

export const updatePromptAdmin = createAsyncThunk<
  Prompt,
  { prompt: PatchPromptOpts; promptId: string }
>('prompts/updatePromptAdmin', async ({ prompt, promptId }, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/admin/prompts/${promptId}`, {
      method: 'PATCH',
      body: JSON.stringify(prompt),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Prompt;
    return response;
  } catch (error) {
    const message = (error as Error)?.message;
    if (message.startsWith('A prompt for this prompt type already exists')) {
      return rejectWithValue('A prompt for this prompt type already exists');
    }
    enqueueSnackbar('Failed to update prompt', { variant: 'error' });
    return rejectWithValue('Failed to update prompt');
  }
});

export const createPrompt = createAsyncThunk<Prompt, CreatePrompt>(
  'prompts/createPrompt',
  async (prompt, { dispatch, rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/prompts`, {
        method: 'POST',
        body: JSON.stringify(prompt),
        headers: {
          'Content-Type': 'application/json',
        },
      })) as Prompt;

      (dispatch as AppDispatch)(findPrompts());

      return response;
    } catch {
      enqueueSnackbar('Failed to post prompt', { variant: 'error' });
      return rejectWithValue('Failed to post prompt');
    }
  }
);

export const createPromptAdmin = createAsyncThunk<Prompt, CreatePrompt>(
  'prompts/createPromptAdmin',
  async (prompt, { dispatch, rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/admin/prompts`, {
        method: 'POST',
        body: JSON.stringify(prompt),
        headers: {
          'Content-Type': 'application/json',
        },
      })) as Prompt;

      (dispatch as AppDispatch)(findPrompts());

      return response;
    } catch (error) {
      const message = (error as Error)?.message;
      if (message.startsWith('A prompt for this prompt type already exists')) {
        return rejectWithValue('A prompt for this prompt type already exists');
      }
      enqueueSnackbar('Failed to post prompt', { variant: 'error' });
      return rejectWithValue('Failed to post prompt');
    }
  }
);

type DeletePromptPayload = {
  promptId: string;
  allVersions?: boolean;
};

export const deletePrompt = createAsyncThunk<void, DeletePromptPayload>(
  'prompts/deletePrompt',
  async ({ promptId, allVersions = true }, { dispatch, rejectWithValue }) => {
    try {
      const url = new URL(`${API_URL}/prompts/${promptId}`);
      url.searchParams.set('allVersions', allVersions.toString());

      await enrichedFetch(url.toString(), {
        method: 'DELETE',
      });

      (dispatch as AppDispatch)(findPrompts());
      (dispatch as AppDispatch)(findFeaturedPrompts());
    } catch {
      enqueueSnackbar('Failed to delete prompt', { variant: 'error' });
      return rejectWithValue('Failed to delete prompt');
    }
  }
);

export const archivePrompt = createAsyncThunk<
  void,
  { promptId: Prompt['id']; value: boolean },
  { dispatch: AppDispatch }
>('prompts/archivePrompt', async (params, { dispatch, rejectWithValue }) => {
  try {
    await enrichedFetch(`${API_URL}/prompts/${params.promptId}/archive`, {
      method: 'PATCH',
      body: JSON.stringify({ archive: params.value }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    (dispatch as AppDispatch)(findPrompts());
    (dispatch as AppDispatch)(findFeaturedPrompts());
    const message = params.value ? 'archived' : 'unarchived';
    enqueueSnackbar(`Prompt ${message} successfully`, {
      variant: 'success',
      autoHideDuration: 4000,
    });
  } catch {
    enqueueSnackbar('Failed to archive prompt', { variant: 'error' });
    return rejectWithValue('Failed to archive prompt');
  }
});

export const findTopPromptsLast30Days = createAsyncThunk<
  Prompt[],
  { numPrompts: number; publishedOnly: boolean },
  { state: RootState }
>(
  'prompts/findTopPromptsLast30Days',
  async ({ numPrompts, publishedOnly }, { rejectWithValue }) => {
    const url = `${API_URL}/prompts/topLast30Days?publishedOnly=${publishedOnly}&numPrompts=${numPrompts}`;

    try {
      const response = (await enrichedFetch(url)) as Prompt[];
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch top 3 prompts from the last 30 days', { variant: 'error' });
      return rejectWithValue('Failed to fetch top 3 prompts');
    }
  }
);

export const getPromptsIncrementally = createAsyncThunk<
  PromptAPIResponse,
  { pageAction?: 'increment' | 'reset' },
  { state: RootState }
>('prompts/getPromptsIncrementally', async (args, { getState, dispatch, rejectWithValue }) => {
  const { page, searchQuery, selectedLabels, limit } = getState().prompts;

  const pageAction = args?.pageAction;

  let newPage = page;
  if (pageAction === 'increment') {
    newPage += 1;
  } else if (pageAction === 'reset') {
    newPage = 1;
  }

  const pageOffset = limit * (newPage - 1);
  dispatch(setPage(newPage));

  const { currentFolder } = getState().folders;
  const { selectedApp } = getState().appStatus;

  dispatch(setIsLoading(true));
  let url = `${API_URL}/prompts?recentOnly=false&sort=${PromptSortOptions.DEFAULT}&offset=${pageOffset}&app=${selectedApp}`;

  if (currentFolder && currentFolder.id !== myFolder.id) {
    url += `&folderId=${currentFolder.id}`;
  }

  if (searchQuery) {
    url += `&q=${searchQuery}`;
  }

  if (limit !== undefined) {
    url += `&limit=${limit}`;
  }

  if (selectedLabels.length) {
    const labelsQuery = stringArrayToURLQueryParam('labels', selectedLabels);
    url += `&${labelsQuery}`;
  }

  try {
    const response = (await enrichedFetch(url)) as PromptAPIResponse;
    dispatch(getPromptlabels());
    dispatch(setIsLoading(false));
    return response;
  } catch {
    enqueueSnackbar('Failed to fetch prompts', { variant: 'error' });
    return rejectWithValue('Failed to fetch prompts');
  }
});
